/**
 * Helper Utility Functions
 *
 * This file contains common utility functions used across the application
 * to avoid code duplication and maintain consistency.
 *
 * All functions in this file are pure utility functions that can be safely
 * reused across different modules without side effects.
 */

import config, { logger } from '../config';

// ============================================================================
// CONSTANTS
// ============================================================================

export const HEALTHCARE_COSTS = {
  SINGLE: 197500,
  MARRIED: 395000
};


// Combined financial constants - all financial planning values in one place
export const FINANCIAL_CONSTANTS = {
  INFLATION_RATE: 0.023,
  INVESTMENT_RETURN_RATE: 0.07,

  CURRENT_VALUE_REAL_ESTATE_RATE: 0.012,
  CURRENT_VALUE_OTHER_ASSETS_RATE: 0.047,

  FUTURE_VALUE_REAL_ESTATE_RATE: 0.035,
  FUTURE_VALUE_OTHER_ASSETS_RATE: 0.07,

  SOCIAL_SECURITY_BENEFIT_AGE: 67,
  RMD_START_AGE: 75,
  LIFE_EXPECTANCY: 95,

  BENEFITS_PERCENTAGE: 80,
  COLLEGE_COST: 250000,

  // Monte Carlo Simulation Constants
  MONTE_CARLO_VOLATILITY: 0.12,
  MONTE_CARLO_SIMULATIONS: 1001,

  // Defaults used when onboarding does not collect these inputs
  DEFAULT_MORTGAGE_INTEREST_RATE: 0.065,
  DEFAULT_MORTGAGE_YEARS_REMAINING: 25,
  DEFAULT_STUDENT_LOAN_INTEREST_RATE: 0.055,
  DEFAULT_STUDENT_LOAN_YEARS_REMAINING: 10,
  DEFAULT_CAR_LOAN_INTEREST_RATE: 0.075,
  DEFAULT_CAR_LOAN_YEARS_REMAINING: 5,
  DEFAULT_OTHER_DEBT_INTEREST_RATE: 0.09,
  DEFAULT_OTHER_DEBT_YEARS_REMAINING: 5,
  DEFAULT_CREDIT_CARD_INTEREST_RATE: 0.21,
  DEFAULT_CREDIT_CARD_PAYMENT_YEARS: 5,
  CREDIT_CARD_PAYMENT_MODE: 'InterestOnly',

  // Housing & education assumptions (backend SimulationInput defaults)
  DEFAULT_HOME_APPRECIATION_RATE: 0.035,
  TUITION_PER_CHILD_PER_YEAR: 62500, // FINANCIAL_CONSTANTS.COLLEGE_COST / 4

  // Career shift defaults
  DEFAULT_CAREER_SHIFT_CHOICE: 'Voluntary',

  // Tax (SimulationInput defaults)
  EFFECTIVE_TAX_RATE: 0.24,
  SS_TAXABLE_PORTION: 0.85,

  // Simulation execution flags
  DEFAULT_RETURN_ANALYSIS_DETAILS: true,
  DEFAULT_RETURN_MC_FAILURE_TRACE: false,
  DEFAULT_EXPENSES_INCLUDE_DEBTS: true,

  // Emergency fund (SimulationInput defaults)
  DEFAULT_EMERGENCY_FUND_AMOUNT: 0,
  DEFAULT_EMERGENCY_FUND_BALANCE: null,
  DEFAULT_EMERGENCY_FUND_DRAWDOWN: true,

  // Runway / career shift display
  FULLY_COVERED: 'Fully Covered',
};

/**
 * Minimum onboarding question IDs required for the financial dashboard to produce meaningful projections.
 * Used by ForecastPage, AdminOnboardingUsersTable, and FinancialDashboard to validate cached data or log warnings.
 * Keys are backend question IDs; values are human-readable labels for messages.
 */
export const DASHBOARD_REQUIRED_FIELDS = {
  'your_year_of_birth': 'Year of birth',
  'retirement_age': 'Retirement age',
  'monthly_expenses': 'Monthly expenses'
};

/**
 * Returns human-readable names of required dashboard fields that are missing from onboarding progress.
 * @param {Object} progress - Normalized onboarding progress (question IDs as keys)
 * @returns {string[]} Missing field labels (e.g. ['Year of birth', 'Monthly expenses'])
 */
export const getMissingRequiredDashboardFields = (progress) => {
  if (!progress || typeof progress !== 'object' || Array.isArray(progress)) return Object.values(DASHBOARD_REQUIRED_FIELDS);
  return Object.entries(DASHBOARD_REQUIRED_FIELDS)
    .filter(([key]) => !progress[key])
    .map(([, name]) => name);
};

// Common AI response instructions for all prompts
export const AI_RESPONSE_INSTRUCTIONS = `
IMPORTANT RESPONSE GUIDELINES:
1. TONE: Maintain a friendly yet professional tone. Avoid using overly friendly words like "Wonderful" too frequently, as this makes responses sound over-friendly and overly sweet. Be polite, professional, and slightly friendly - not over-friendly.
2. NO NUMBERS OR PERCENTAGES: Do not mention any percentages, numbers, or specific figures in your response. Avoid statements like "21% mark in planning" or any numerical references. Keep responses qualitative and descriptive without specific metrics.`;

// ============================================================================
// USER HELPERS
// ============================================================================

/**
 * Extracts the first name from a full name stored in localStorage.
 * Returns only the first word (first name) from the full name.
 *
 * @param {string} [fullName] - Full name from localStorage (can be null/undefined)
 * @returns {string} - First name only, or empty string if no name provided
 */
export const getFirstName = (fullName) => {
  if (!fullName || typeof fullName !== 'string') return '';
  // Split on any whitespace to handle multiple spaces/tabs/newlines robustly
  return fullName.trim().split(/\s+/)[0] || '';
};

/**
 * Gets the user's first name from localStorage.
 *
 * @returns {string} - First name only, or empty string if no name in localStorage
 */
export const getFirstNameFromStorage = () => {
  const fullName = localStorage.getItem(config.STORAGE_KEYS.NAME) || '';
  return getFirstName(fullName);
};

/**
 * Gets the user's email from localStorage.
 *
 * @returns {string} - Trimmed email, or empty string if none is stored
 */
export const getEmailFromStorage = () => {
  const email = localStorage.getItem(config.STORAGE_KEYS.EMAIL) || '';
  return typeof email === 'string' ? email.trim() : '';
};

/**
 * Persists authenticated user fields used across the app (token, name, email).
 *
 * @param {{ token?: string, name?: string, email?: string }} [session]
 */
export const persistAuthUser = ({ token, name, email } = {}) => {
  if (typeof localStorage === 'undefined') return;
  if (token) localStorage.setItem(config.STORAGE_KEYS.TOKEN, token);
  if (name) localStorage.setItem(config.STORAGE_KEYS.NAME, name);
  const trimmedEmail = typeof email === 'string' ? email.trim() : '';
  if (trimmedEmail) localStorage.setItem(config.STORAGE_KEYS.EMAIL, trimmedEmail);
};

/**
 * Remembers the name collected before account creation so a later visit
 * can skip the name screen and open the onboarding agent.
 *
 * @param {string} name
 */
export const rememberSignupCollectedName = (name) => {
  if (typeof localStorage === 'undefined') return;
  const trimmed = typeof name === 'string' ? name.trim() : '';
  if (!trimmed) return;
  localStorage.setItem(config.STORAGE_KEYS.SIGNUP_COLLECTED_NAME, trimmed);
};

/**
 * Returns true when `accountName` is the name already collected during signup.
 * Clears the stored name when it matches.
 *
 * @param {string} accountName
 * @returns {boolean}
 */
export const matchesSignupCollectedName = (accountName) => {
  if (typeof localStorage === 'undefined') return false;
  const collected = (localStorage.getItem(config.STORAGE_KEYS.SIGNUP_COLLECTED_NAME) || '').trim();
  const account = typeof accountName === 'string' ? accountName.trim() : '';
  if (!collected || !account || collected.toLowerCase() !== account.toLowerCase()) return false;
  localStorage.removeItem(config.STORAGE_KEYS.SIGNUP_COLLECTED_NAME);
  return true;
};

// ============================================================================
// FAMILY STATUS HELPERS
// ============================================================================

/**
 * Returns whether the family situation indicates a married or partnered status.
 * Uses flexible, case-insensitive matching: true if the value contains
 * "married", "partner", or "spouse". Safe for null, undefined, and non-strings.
 *
 * @param {string} [familySituation] - Family situation (e.g. from family_situation)
 * @returns {boolean}
 */
export const isMarried = (familySituation) => {
  if (familySituation == null || typeof familySituation !== 'string') return false;
  const s = familySituation.toLowerCase().trim();
  return s.includes('married') || s.includes('partner') || s.includes('spouse');
};

// ============================================================================
// NUMERIC PARSING
// ============================================================================

const SUFFIX_PATTERNS = [
  { pattern: /(\d+(?:\.\d+)?)\s*[mM](?:\s*(?:million|mil))?/i, multiplier: 1000000 },
  { pattern: /(\d+(?:\.\d+)?)\s*[kK](?:\s*(?:thousand|k))?/i, multiplier: 1000 },
  { pattern: /(\d+(?:\.\d+)?)\s*thousand/i, multiplier: 1000 },
  { pattern: /(\d+(?:\.\d+)?)\s*million/i, multiplier: 1000000 }
];

export const parseNumericValue = (value, defaultValue = 0) => {
  if (typeof value === 'number') {
    return isNaN(value) ? defaultValue : value;
  }

  if (!value || String(value).trim() === '') {
    return defaultValue;
  }

  let cleanText = String(value).trim().replace(/^[$€£¥₹]/, '').trim();

  // Try suffix patterns first
  for (const { pattern, multiplier } of SUFFIX_PATTERNS) {
    const match = cleanText.match(pattern);
    if (match) {
      const num = parseFloat(match[1]);
      if (!isNaN(num)) return num * multiplier;
    }
  }

  cleanText = cleanText.replace(/,/g, '');
  const numberMatch = cleanText.match(/(\d+(?:\.\d+)?)/);

  if (numberMatch) {
    const parsed = parseFloat(numberMatch[1]);
    if (!isNaN(parsed)) {
      const afterNumber = cleanText.substring(numberMatch.index + numberMatch[0].length).trim();
      if (/^[kK](?:\s*(?:thousand|k))?/i.test(afterNumber)) return parsed * 1000;
      if (/^[mM](?:\s*(?:million|mil))?/i.test(afterNumber)) return parsed * 1000000;
      return parsed;
    }
  }

  const finalParsed = parseFloat(cleanText);
  return isNaN(finalParsed) ? defaultValue : finalParsed;
};

// ============================================================================
// FINANCIAL CALCULATIONS
// ============================================================================

/**
 * Calculates current age from birth year (YYYY).
 *
 * @param {string} birthYear - Birth year, 4-digit "YYYY" (e.g. "1991")
 * @returns {number|null} Current age or null if invalid
 */
export const calculateAgeFromBirthYear = (birthYear) => {
  if (!birthYear) return null;

  const str = String(birthYear).trim();
  if (!/^\d{4}$/.test(str)) {
    logger.warn('Invalid birth year (expected YYYY):', birthYear);
    return null;
  }

  const year = parseInt(str, 10);
  if (year < 1900 || year > 2100) {
    logger.warn('Invalid birth year (must be 1900–2100):', birthYear);
    return null;
  }

  return new Date().getFullYear() - year;
};

// Asset and debt field mappings
export const ASSET_FIELDS = [
  'childrenCollegeSavings',
  'retirementAccounts',
  'totalBalancePostTaxRetirementAccounts',
  'totalBalanceEquityCompensationAccounts',
  'otherInvestments',
  'homeEstimatedValue',
  'currentMarketValue'
];

const DEBT_FIELDS = [
  'homeMortgageDebt',
  'carLoanDebt',
  'educationalLoan',
  'oweCreditCards',
  'otherDebtsLoans'
];

export const ASSET_SOURCE_FIELDS = [
  'children_college_savings',
  'retirement_accounts',
  'total_balance_post_tax_retirement_accounts',
  'total_balance_equity_compensation_accounts',
  'other_investments',
  'home_estimated_value',
  'current_market_value'
];

export const DEBT_SOURCE_FIELDS = [
  'home_mortgage_debt',
  'car_loan_debt',
  'educational_loan',
  'owe_credit_cards',
  'other_debts_loans'
];

export const extractAssetsAndDebts = (onboardingProgress) => {
  const assets = ASSET_FIELDS.reduce((acc, field, index) => {
    acc[field] = parseNumericValue(onboardingProgress[ASSET_SOURCE_FIELDS[index]]);
    return acc;
  }, {});

  const debts = DEBT_FIELDS.reduce((acc, field, index) => {
    acc[field] = parseNumericValue(onboardingProgress[DEBT_SOURCE_FIELDS[index]]);
    return acc;
  }, {});

  return { assets, debts };
};


// ============================================================================
// ONBOARDING CATEGORY MAPPING
// ============================================================================

const CATEGORY_MAPPINGS = {
  '1': { name: 'Self Assessment and Career Shift Readiness', key: 'self-assessment' },
  '2': { name: 'Personal & Background Info', key: 'personal-profile' },
  '3': { name: 'Income and Expenses', key: 'income-expenses' },
  '4': { name: 'Investments and Assets', key: 'investments-assets' },
  '5': { name: 'Debts and Liabilities', key: 'debts-liabilities' }
};

const KEYWORD_MAP = {
  'self-assessment': ['self', 'assessment', 'career'],
  'personal-profile': ['personal', 'profile', 'background', 'family', 'location', 'geography', 'demography'],
  'income-expenses': ['income', 'expense'],
  'investments-assets': ['investment', 'asset'],
  'debts-liabilities': ['debt', 'liabilit']
};

/**
 * Gets category name map for onboarding agent
 * @returns {Object} Map of category prefixes to category names
 */
const getOnboardingAgentCategoryNameMap = () => {
  return Object.fromEntries(
    Object.entries(CATEGORY_MAPPINGS).map(([key, value]) => [key, value.name])
  );
};

const findSectionKeyByKeywords = (input) => {
  for (const [key, keywords] of Object.entries(KEYWORD_MAP)) {
    if (keywords.some(keyword => input.includes(keyword))) {
      return key;
    }
  }
  return 'other';
};

/**
 * Maps category name or prefix to section key
 * @param {string} categoryNameOrPrefix - Category name or numeric prefix
 * @returns {string} Section key for the category
 */
export const mapCategoryToSectionKey = (categoryNameOrPrefix) => {
  if (!categoryNameOrPrefix) return 'other';

  const input = String(categoryNameOrPrefix).trim().toLowerCase().replace(/&/g, 'and');

  // Direct prefix match
  if (CATEGORY_MAPPINGS[input]) {
    return CATEGORY_MAPPINGS[input].key;
  }

  // Exact category name match
  const normalizedName = input;
  for (const mapping of Object.values(CATEGORY_MAPPINGS)) {
    if (mapping.name.toLowerCase().replace(/&/g, 'and') === normalizedName) {
      return mapping.key;
    }
  }

  // Keyword matching
  return findSectionKeyByKeywords(input);
};

// ============================================================================
// CHAT DATA EXTRACTION
// ============================================================================

export const extractChatQuestionsAndAnswers = (chatData) => {
  const answersMap = {};
  const questionsMap = {};

  if (!Array.isArray(chatData)) {
    return { answersMap, questionsMap };
  }

  chatData.forEach((message) => {
    if (message.id) {
      if (message.type === 'question') questionsMap[message.id] = message.text;
      if (message.type === 'answer') answersMap[message.id] = message.text;
    }
  });

  return { answersMap, questionsMap };
};

export const createCategoriesFromChatQuestions = (questionsMap) => {
  const categoryNameMap = getOnboardingAgentCategoryNameMap();
  const questionsByCategory = {};

  Object.keys(questionsMap).sort().forEach(id => {
    const prefix = id.split('_')[0];
    if (!questionsByCategory[prefix]) {
      questionsByCategory[prefix] = [];
    }
    questionsByCategory[prefix].push({
      id,
      title: questionsMap[id],
      type: 'free_text'
    });
  });

  return Object.keys(questionsByCategory).sort().map(prefix => ({
    id: prefix,
    name: categoryNameMap[prefix] || `Category ${prefix}`,
    questions: questionsByCategory[prefix]
  }));
};

export const saveOnboardingAIChatToStorage = (apiResponse, appConfig) => {
  try {
    const chatData = apiResponse?.data?.v2_onboarding_ai?.chat;
    if (!chatData) {
      return false;
    }

    const filteredChat = chatData.filter(
      (message) => message.type === 'question' || message.type === 'answer'
    );

    if (filteredChat.length > 0) {
      localStorage.setItem(appConfig.STORAGE_KEYS.ONBOARDING_AI_CHAT, JSON.stringify(filteredChat));
      return true;
    }

    return false;
  } catch (error) {
    logger.error('Error saving onboarding AI chat to localStorage:', error);
    return false;
  }
};

// ============================================================================
// TEXT FORMATTING
// ============================================================================

const escapeHtml = (text) => {
  if (typeof text !== 'string') return text;
  const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
  return text.replace(/[&<>"']/g, (m) => map[m]);
};

const formatMarkdown = (text) => {
  return text
    .replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+?)`/g, '<code>$1</code>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
};

export const parseBoldText = (text) => {
  if (!text || typeof text !== 'string') {
    return [{ type: 'text', content: text || '' }];
  }

  const parts = [];
  const boldRegex = /\*\*([^*]+)\*\*/g;
  let lastIndex = 0;
  let match;

  while ((match = boldRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: text.substring(lastIndex, match.index) });
    }
    parts.push({ type: 'bold', content: match[1] });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push({ type: 'text', content: text.substring(lastIndex) });
  }

  return parts.length > 0 ? parts : [{ type: 'text', content: text }];
};

export const parseTextWithBullets = (text) => {
  if (!text || typeof text !== 'string') return [];

  return text.split('\n')
    .filter(line => line.trim())
    .map(line => {
      const trimmed = line.trim();
      const isBullet = /^[•\-*]/.test(trimmed);
      const content = isBullet ? trimmed.substring(1).trim() : trimmed;

      return {
        isBullet,
        content,
        parts: parseBoldText(content),
        originalLine: line
      };
    });
};

export const parseAIResponseJSON = (rawResponse) => {
  if (!rawResponse || typeof rawResponse !== 'string') {
    return { answer: rawResponse || '', suggestedResponses: null, isParsed: false };
  }

  try {
    let jsonString = rawResponse.trim();
    if (jsonString.startsWith('```json')) {
      jsonString = jsonString.replace(/^```json\s*/, '').replace(/\s*```$/, '');
    } else if (jsonString.startsWith('```')) {
      jsonString = jsonString.replace(/^```\s*/, '').replace(/\s*```$/, '');
    }

    const parsed = JSON.parse(jsonString);
    if (parsed && typeof parsed === 'object') {
      const suggestedResponses = Array.isArray(parsed.suggested_responses) && parsed.suggested_responses.length > 0
        ? parsed.suggested_responses.filter(s => s && typeof s === 'string' && s.trim() !== '')
        : null;

      return {
        answer: parsed.answer && typeof parsed.answer === 'string' ? parsed.answer : rawResponse,
        suggestedResponses: suggestedResponses?.length > 0 ? suggestedResponses : null,
        isParsed: true
      };
    }
  } catch (parseError) {
    logger.error('Failed to parse AI response as JSON, using raw response:', parseError);
  }

  return { answer: rawResponse, suggestedResponses: null, isParsed: false };
};

const processListItems = (lines, pattern) => {
  const items = lines
    .map(line => {
      const match = line.trim().match(pattern);
      return match ? formatMarkdown(match[1]) : null;
    })
    .filter(Boolean);

  return items.length > 0 ? `<ul>${items.map(item => `<li>${item}</li>`).join('')}</ul>` : '';
};

export const formatResponse = (text) => {
  if (!text || !text.trim()) return '<p>No response</p>';

  // First, decode HTML entities (like &#039; to ')
  let decodedText = decodeHtmlEntities(String(text).normalize('NFC'));

  // Check if the text already contains HTML tags
  const hasHtmlTags = /<[^>]+>/.test(decodedText);

  // If it already contains HTML tags, return it as-is (already formatted)
  if (hasHtmlTags) {
    return decodedText;
  }

  // Otherwise, process it as plain text with markdown formatting
  let formatted = escapeHtml(decodedText);
  formatted = formatted.replace(/^---+$/gm, '<hr class="response-divider" />');

  const blocks = formatted.split(/\n\n+/);
  const processedBlocks = blocks.map(block => {
    const trimmed = block.trim();
    if (!trimmed) return '';

    const lines = trimmed.split('\n');

    // Check for headings at the start of any line (not just block start)
    const headingLines = lines.filter(line => /^(#{1,6})\s+/.test(line.trim()));
    if (headingLines.length > 0) {
      // Process block with headings
      const result = [];
      let currentParagraph = [];
      let inList = false;
      let listItems = [];
      let listPattern = null;

      lines.forEach((line, index) => {
        const trimmedLine = line.trim();

        // Check for heading
        const headingMatch = trimmedLine.match(/^(#{1,6})\s+(.+)$/);
        if (headingMatch) {
          // Flush any pending content
          if (currentParagraph.length > 0) {
            result.push(`<p>${formatMarkdown(currentParagraph.join(' '))}</p>`);
            currentParagraph = [];
          }
          if (inList && listItems.length > 0) {
            result.push(`<ul>${listItems.map(item => `<li>${formatMarkdown(item)}</li>`).join('')}</ul>`);
            listItems = [];
            inList = false;
            listPattern = null;
          }
          const level = Math.min(headingMatch[1].length, 6);
          result.push(`<h${level}>${formatMarkdown(headingMatch[2])}</h${level}>`);
          return;
        }

        // Check for bullet list
        const bulletMatch = trimmedLine.match(/^[-*•]\s+(.+)$/);
        if (bulletMatch) {
          if (currentParagraph.length > 0) {
            result.push(`<p>${formatMarkdown(currentParagraph.join(' '))}</p>`);
            currentParagraph = [];
          }
          if (!inList || listPattern !== 'bullet') {
            if (inList && listItems.length > 0) {
              result.push(`<ul>${listItems.map(item => `<li>${formatMarkdown(item)}</li>`).join('')}</ul>`);
            }
            listItems = [];
            inList = true;
            listPattern = 'bullet';
          }
          listItems.push(bulletMatch[1]);
          return;
        }

        // Check for numbered list
        const numberedMatch = trimmedLine.match(/^(\d+\.)\s+(.+)$/);
        if (numberedMatch) {
          if (currentParagraph.length > 0) {
            result.push(`<p>${formatMarkdown(currentParagraph.join(' '))}</p>`);
            currentParagraph = [];
          }
          if (!inList || listPattern !== 'numbered') {
            if (inList && listItems.length > 0) {
              result.push(`<ol>${listItems.map(item => `<li>${formatMarkdown(item)}</li>`).join('')}</ol>`);
            }
            listItems = [];
            inList = true;
            listPattern = 'numbered';
          }
          listItems.push(numberedMatch[2]);
          return;
        }

        // Regular text line
        if (inList) {
          // Flush list
          if (listPattern === 'bullet') {
            result.push(`<ul>${listItems.map(item => `<li>${formatMarkdown(item)}</li>`).join('')}</ul>`);
          } else if (listPattern === 'numbered') {
            result.push(`<ol>${listItems.map(item => `<li>${formatMarkdown(item)}</li>`).join('')}</ol>`);
          }
          listItems = [];
          inList = false;
          listPattern = null;
        }
        if (trimmedLine) {
          currentParagraph.push(trimmedLine);
        }
      });

      // Flush remaining content
      if (inList && listItems.length > 0) {
        if (listPattern === 'bullet') {
          result.push(`<ul>${listItems.map(item => `<li>${formatMarkdown(item)}</li>`).join('')}</ul>`);
        } else if (listPattern === 'numbered') {
          result.push(`<ol>${listItems.map(item => `<li>${formatMarkdown(item)}</li>`).join('')}</ol>`);
        }
      }
      if (currentParagraph.length > 0) {
        result.push(`<p>${formatMarkdown(currentParagraph.join(' '))}</p>`);
      }

      return result.join('\n');
    }

    // Numbered lists (when no headings in block)
    if (lines.some(line => /^\d+\.\s+/.test(line.trim()))) {
      return processListItems(lines, /^\d+\.\s+(.+)$/);
    }

    // Bullet lists (when no headings in block)
    if (lines.some(line => /^[-*•]\s+/.test(line.trim()))) {
      return processListItems(lines, /^[-*•]\s+(.+)$/);
    }

    // Horizontal rule
    if (trimmed.includes('<hr class="response-divider" />')) {
      return trimmed;
    }

    // Paragraph
    return `<p>${formatMarkdown(trimmed.replace(/\n/g, '<br />'))}</p>`;
  });

  const result = processedBlocks.filter(Boolean).join('\n');
  return result || `<p>${formatMarkdown(escapeHtml(decodedText).replace(/\n/g, '<br />'))}</p>`;
};

/** Empty <em> (optional attrs), including whitespace / nbsp / ZWSP-only content. */
const TRIM_MESSAGE_EMPTY_EM_RE = /<em\b[^>]*>(?:\s|&nbsp;|&#160;|&#8203;|&#x200B;|&#xFEFF;)*<\/em>/gi;

/** Trailing <br> variants with surrounding whitespace (end of string only). */
const TRIM_MESSAGE_TRAILING_BR_RE =
  /(?:\s|&nbsp;|&#160;)*(?:<br\b[^>]*\/?>(?:\s|&nbsp;|&#160;)*)+$/i;

/**
 * Trim chat bubble HTML: remove empty <em> and trailing <br> / whitespace.
 * @param {string|null|undefined} html
 * @returns {string}
 */
export const trimMessageContentHtml = (html) => {
  if (html == null) return '';
  let s = String(html);
  if (!s) return s;
  let prev;
  do {
    prev = s;
    s = s.replace(TRIM_MESSAGE_EMPTY_EM_RE, '');
  } while (s !== prev);
  do {
    prev = s;
    s = s.replace(TRIM_MESSAGE_TRAILING_BR_RE, '');
  } while (s !== prev);
  return s.replace(/\s+$/u, '');
};

// ============================================================================
// SOCIAL SECURITY BENEFITS
// ============================================================================

/**
 * Social Security benefits lookup table (monthly amounts)
 * Ages 62-69: specific values
 * Ages 70+: step function (70-74 use 70, 75-79 use 75, etc.)
 */
const SOCIAL_SECURITY_LOOKUP = {
  62: 1298,
  63: 1338,
  64: 1459,
  65: 1563,
  66: 1739,
  67: 1883,
  68: 1948,
  69: 1945,
  70: 2037,  // Ages 70-74 use this value
  75: 2012,  // Ages 75-79 use this value
  80: 1940,  // Ages 80-84 use this value
  85: 1864,  // Ages 85-89 use this value
  90: 1767   // Ages 90+ use this value
};

/**
 * Gets monthly Social Security benefit amount for a given age
 * Uses lookup table with step-function logic for ages between data points
 * @param {number} age - Age to get benefit for
 * @returns {number} Monthly Social Security benefit amount (0 if age < 62)
 */
export const getSocialSecurityMonthlyBenefit = (age) => {
  if (age < 62) return 0;
  if (age >= 90) return SOCIAL_SECURITY_LOOKUP[90];
  if (age >= 85) return SOCIAL_SECURITY_LOOKUP[85];
  if (age >= 80) return SOCIAL_SECURITY_LOOKUP[80];
  if (age >= 75) return SOCIAL_SECURITY_LOOKUP[75];
  if (age >= 70) return SOCIAL_SECURITY_LOOKUP[70];
  // Ages 62-69: use exact lookup
  return SOCIAL_SECURITY_LOOKUP[age] || 0;
};

// ============================================================================
// NUMBER FORMATTING
// ============================================================================

export const formatNumberEnUS = (value, options = {}) => {
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (!isFinite(num)) return '0';
  return num.toLocaleString('en-US', options);
};

// ============================================================================
// USER UTILITIES
// ============================================================================

/**
 * Gets user initials from name (from localStorage or provided name)
 * @param {string} [name] - Optional name to use instead of localStorage
 * @returns {string} User initials (1-2 characters, uppercase)
 */
export const getUserInitials = (name = null) => {
  // If name is provided, use it; otherwise get from localStorage
  const userName = name || localStorage.getItem(config.STORAGE_KEYS.NAME) || '';

  const trimmed = typeof userName === 'string' ? userName.trim() : '';
  if (!trimmed) {
    return 'U'; // Default to 'U' if no name
  }

  const nameParts = trimmed.split(/\s+/);
  if (nameParts.length >= 2) {
    // If full name (first + last), use first letter of each
    return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
  } else {
    // If only one name, use first letter
    return nameParts[0][0].toUpperCase();
  }
};

// ============================================================================
// PASSWORD STRENGTH VALIDATION
// ============================================================================

/**
 * Validates password strength based on:
 * - One upper case at least
 * - One special character at least
 * - One numeric at least
 * - Alphanumeric (at least one letter)
 * - Min 8 characters, max 64 characters
 * - No spaces
 *
 * @param {string} password - Password to validate
 * @returns {Object} Password strength validation result with requirements
 */
export const validatePasswordStrength = (password) => {
  const emptyRequirements = {
    uppercase: { text: 'One upper case at least', fulfilled: false },
    special: { text: 'One special character at least', fulfilled: false },
    numeric: { text: 'One numeric at least', fulfilled: false },
    alpha: { text: 'Alphanumeric', fulfilled: false },
    length: { text: 'Min 8 characters, max 64 characters', fulfilled: false },
    noSpaces: { text: 'No spaces', fulfilled: false }
  };

  if (!password) {
    return {
      strength: 'weak',
      score: 0,
      percentage: 0,
      totalCriteria: 6,
      requirements: emptyRequirements
    };
  }

  const uppercaseFulfilled = /[A-Z]/.test(password);
  const specialFulfilled = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);
  const numericFulfilled = /\d/.test(password);
  const alphaFulfilled = /[a-zA-Z]/.test(password);
  const lengthFulfilled = password.length >= 8 && password.length <= 64;
  const noSpacesFulfilled = !/\s/.test(password);

  const requirements = {
    uppercase: { text: 'One upper case at least', fulfilled: uppercaseFulfilled },
    special: { text: 'One special character at least', fulfilled: specialFulfilled },
    numeric: { text: 'One numeric at least', fulfilled: numericFulfilled },
    alpha: { text: 'Alphanumeric', fulfilled: alphaFulfilled },
    length: { text: 'Min 8 characters, max 64 characters', fulfilled: lengthFulfilled },
    noSpaces: { text: 'No spaces', fulfilled: noSpacesFulfilled }
  };

  const totalCriteria = 6;
  const score = [
    uppercaseFulfilled,
    specialFulfilled,
    numericFulfilled,
    alphaFulfilled,
    lengthFulfilled,
    noSpacesFulfilled
  ].filter(Boolean).length;
  const percentage = totalCriteria > 0 ? (score / totalCriteria) * 100 : 0;

  let strength;
  if (score === totalCriteria) {
    strength = 'very strong';
  } else if (score >= 4) {
    strength = 'strong';
  } else {
    strength = 'weak';
  }

  return {
    strength,
    score,
    percentage,
    totalCriteria,
    requirements
  };
};

/**
 * Gets color code for password strength indicator
 *
 * @param {string} strength - Password strength ('weak', 'strong', 'very strong')
 * @returns {string} Color code for the strength level
 */
export const getStrengthColor = (strength) => {
  switch (strength) {
    case 'weak':
      return '#ff8c00'; // Orange
    case 'strong':
      return '#044766'; // Blue
    case 'very strong':
      return '#28a745'; // Green
    default:
      return '#6c757d'; // Gray
  }
};

/**
 * Gets display text for password strength
 *
 * @param {string} strength - Password strength ('weak', 'strong', 'very strong')
 * @returns {string} Display text for the strength level
 */
export const getStrengthText = (strength) => {
  switch (strength) {
    case 'weak':
      return 'Weak';
    case 'strong':
      return 'Strong';
    case 'very strong':
      return 'Very Strong';
    default:
      return 'Weak';
  }
};

/**
 * Gets guidance message based on password strength
 *
 * @param {string} strength - Password strength ('weak', 'strong', 'very strong')
 * @returns {string} Guidance message for the user
 */
export const getStrengthGuidance = (strength) => {
  switch (strength) {
    case 'weak':
      return 'Try another one';
    case 'strong':
      return 'Getting stronger! Meet all requirements for a secure password';
    case 'very strong':
      return 'Great job! 🔐 This password is strong and secure';
    default:
      return 'Try another one';
  }
};

/**
 * Gets color code for requirement fulfillment status
 *
 * @param {boolean} fulfilled - Whether the requirement is fulfilled
 * @returns {string} Color code (green when fulfilled, gray when not)
 */
export const getRequirementColor = (fulfilled) => {
  return fulfilled ? '#28a745' : '#6c757d'; // Green when fulfilled, gray when not
};

// ============================================================================
// HTML ENTITIES & ONBOARDING DATA UTILITIES
// ============================================================================

/**
 * Decode HTML entities in text
 * @param {string} text - Text containing HTML entities
 * @returns {string} Decoded text
 */
export const decodeHtmlEntities = (text) => {
  if (!text) return text;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

/**
 * The v2 onboarding agent opens once onboarding is finished or chat progress exists.
 * Signup and first-time Google collect the name before the extended welcome,
 * then enter this agent after that commitment.
 *
 * @param {object|null|undefined} userData Row from GET /api/v2/onboarding-ai `data`
 * @returns {boolean}
 */
export const canEnterV2OnboardingAgent = (userData) => {
  if (!userData || typeof userData !== 'object') return false;
  if (userData.onboarding_ai_done) return true;
  const chat = userData.v2_onboarding_ai?.chat;
  return Array.isArray(chat) && chat.length > 0;
};
